import { Type, type Static } from "@sinclair/typebox";
import { PixKeyType } from "./driver.ts";

/**
 * Staff permission vocabulary (architecture § Identity): a role × module matrix of actions,
 * scoped by city. Roles are rows in `staff_roles`; this is the closed set they draw from.
 */
export const STAFF_MODULES = ["corridas", "motoristas", "passageiros", "financeiro", "repasses", "seguranca", "tarifas", "equipe", "suporte"] as const;
export type StaffModule = (typeof STAFF_MODULES)[number];

export const STAFF_ACTIONS = ["ver", "editar", "aprovar", "exportar"] as const;
export type StaffAction = (typeof STAFF_ACTIONS)[number];

export type StaffPermissions = Partial<Record<StaffModule, StaffAction[]>>;

/** `staff_members.city_ids` holds this sentinel for members who see every city. */
export const ALL_CITIES = "*";

export function can(permissions: StaffPermissions, module: StaffModule, action: StaffAction): boolean {
  return permissions[module]?.includes(action) ?? false;
}

/** Whether a member scoped to `cityIds` may act on `cityId`. */
export function inCityScope(cityIds: readonly string[], cityId: string): boolean {
  return cityIds.includes(ALL_CITIES) || cityIds.includes(cityId);
}

// ── Payouts (Admin-05 Repasses Pix, driver "Sacar agora via Pix") ────────────────────────

export const PayoutItemStatus = Type.Union(
  [Type.Literal("ready"), Type.Literal("held"), Type.Literal("sent"), Type.Literal("paid"), Type.Literal("failed"), Type.Literal("retrying")],
  { $id: "PayoutItemStatus" },
);
export type PayoutItemStatus = Static<typeof PayoutItemStatus>;

export const PayoutBatchStatus = Type.Union(
  [Type.Literal("draft"), Type.Literal("approved"), Type.Literal("processing"), Type.Literal("paused"), Type.Literal("completed")],
  { $id: "PayoutBatchStatus" },
);
export type PayoutBatchStatus = Static<typeof PayoutBatchStatus>;

export const WithdrawalInput = Type.Object(
  { amountCents: Type.Optional(Type.Integer({ minimum: 1, description: "Debited from the balance; default: the whole available balance" })) },
  { $id: "WithdrawalInput" },
);
export type WithdrawalInput = Static<typeof WithdrawalInput>;

/** An instant withdrawal ("Saque na hora"): one payout_items row without a batch. */
export const WithdrawalView = Type.Object(
  {
    id: Type.String(),
    /** What the Pix sends (the amount debited minus `feeCents`). */
    amountCents: Type.Integer(),
    feeCents: Type.Integer(),
    status: PayoutItemStatus,
    pixKeyType: PixKeyType,
    pixKeyMasked: Type.String(),
    failureReason: Type.Union([Type.String(), Type.Null()]),
    paidAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    createdAt: Type.String({ format: "date-time" }),
  },
  { $id: "WithdrawalView" },
);
export type WithdrawalView = Static<typeof WithdrawalView>;
